<template>
  <div
    class="modal fade"
    id="modalPlatformOrder"
    ref="modal"
    data-bs-backdrop="static"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">
        <div class="modal-header border-0 pb-2">
          <h5 class="modal-title fw-bold text-dark">
            <i class="fa-solid fa-mobile-screen-button me-2 text-theme"/>
            {{ $t('_delivery.New Platform Order') }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"/>
        </div>

        <div class="modal-body px-4 pb-4">
          <div class="mb-3">
            <label class="form-label small fw-bold text-muted mb-2">
              {{ $t('_delivery.Delivery Channel') }}
              <i v-if="loadingPlatforms" class="fa-solid fa-spinner fa-spin ms-1"/>
            </label>
            <div class="d-flex flex-wrap gap-2">
              <button
                v-for="p in platforms"
                :key="p.id"
                type="button"
                class="btn fw-bold rounded-3 px-3 py-2"
                :class="form.delivery_platform_id === p.id ? 'text-white' : 'btn-outline-secondary'"
                :style="form.delivery_platform_id === p.id && p.color ? { backgroundColor: p.color, borderColor: p.color } : {}"
                @click="form.delivery_platform_id = p.id"
              >
                <i v-if="p.icon" :class="p.icon" class="me-1"/>{{ p.name }}
              </button>
            </div>
            <div v-if="!loadingPlatforms && !platforms.length" class="text-muted small mt-1">
              {{ $t('_delivery.No external delivery channels configured yet') }}
            </div>
          </div>

          <div class="mb-3" v-if="selectedPlatform && selectedPlatform.has_pickup">
            <label class="form-label small fw-bold text-muted mb-2">
              {{ $t('_delivery.How does the order leave the store') }}
            </label>
            <div class="row g-2">
              <div class="col-6">
                <button
                  class="btn w-100 py-2 fw-bold rounded-3"
                  :class="!form.is_pickup ? 'btn-theme text-white' : 'btn-outline-secondary'"
                  @click="form.is_pickup = false"
                >
                  <i class="fa-solid fa-motorcycle mb-1"/>
                  <span class="small d-block">{{ $t('_delivery.Rider pickup') }}</span>
                </button>
              </div>
              <div class="col-6">
                <button
                  class="btn w-100 py-2 fw-bold rounded-3"
                  :class="form.is_pickup ? 'btn-theme text-white' : 'btn-outline-secondary'"
                  @click="form.is_pickup = true"
                >
                  <i class="fa-solid fa-person-walking mb-1"/>
                  <span class="small d-block">{{ $t('_delivery.Customer self pickup') }}</span>
                </button>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label small fw-bold text-muted">
              <i class="fa-solid fa-hashtag me-1"/>{{ $t('_delivery.Platform Order No') }}
              <span v-if="selectedPlatform && selectedPlatform.requires_external_ref" class="text-danger">*</span>
            </label>
            <input
              type="text"
              class="form-control shadow-none"
              v-model.trim="form.external_order_ref"
              :placeholder="$t('_delivery.Order no from the platform app') + '...'"
            >
          </div>

          <div>
            <label class="form-label small fw-bold text-muted">
              <i class="fa-solid fa-comment-dots me-1"/>{{ $t('Note') }}
            </label>
            <input
              type="text"
              class="form-control shadow-none"
              v-model.trim="form.note"
              :placeholder="$t('e.g. No spicy, extra sauce')+'...'"
            >
          </div>
        </div>

        <div class="modal-footer border-0 pt-0 px-4 pb-4">
          <button type="button" class="btn btn-outline-secondary rounded-3 px-4" data-bs-dismiss="modal">
            {{ $t('Cancel') }}
          </button>
          <button
            class="btn btn-theme text-white fw-bold rounded-3 px-4 flex-grow-1"
            :disabled="!canSave || loadingSave"
            @click="handleSave"
          >
            <i class="fa-solid fa-spinner fa-spin me-2" v-if="loadingSave"/>
            <i class="fa-solid fa-check me-2" v-else/>
            {{ $t('_delivery.Start keying order') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Sessions, POSDeliveryPlatforms } from '@/services/endpoint/pos.service';
import { useShopStore } from '@/stores/shop.store';

/**
 * เปิดออเดอร์จาก delivery platform (docs ฝั่ง api: delivery-platform-pricing.md §7)
 * - เลือกแค่ platform — type = delivery เสมอ (วิธีรับของไม่มีผลกับเงิน/ราคา
 *   ร้านที่ GP pickup ต่างจริงให้สร้างเป็นช่องทางแยกในหน้าจัดการ)
 * - เปิด session สำเร็จ → เข้าหน้าคีย์เมนูทันที (parent จัดการ navigate)
 */
export default {
  name: 'ModalNewPlatformOrder',
  emits: ['close', 'saved'],
  data() {
    return {
      loadingSave: false,
      loadingPlatforms: true,
      platforms: [],
      form: {
        delivery_platform_id: null,
        is_pickup: false,
        external_order_ref: '',
        note: ''
      }
    };
  },
  computed: {
    selectedPlatform() {
      return this.platforms.find(p => p.id === this.form.delivery_platform_id) || null;
    },
    canSave() {
      if (!this.selectedPlatform) return false;
      if (this.selectedPlatform.requires_external_ref && !this.form.external_order_ref) return false;
      return true;
    }
  },
  methods: {
    async fetchPlatforms() {
      this.loadingPlatforms = true;
      await POSDeliveryPlatforms.list().then(res => {
        // หน้านี้ = ออเดอร์จากแอปเท่านั้น (ส่งเอง/หน้าร้านใช้ New Session ปกติ)
        this.platforms = (res.data?.data ?? []).filter(p => p.is_external);
        if (this.platforms.length === 1) {
          this.form.delivery_platform_id = this.platforms[0].id;
        }
      }).catch(() => {
        this.platforms = [];
      }).finally(() => {
        this.loadingPlatforms = false;
      });
    },
    async handleSave() {
      if (this.loadingSave || !this.canSave) return;
      this.loadingSave = true;
      const payload = {
        // ออเดอร์ platform = delivery เสมอ — วิธีรับของ (ไรเดอร์/ลูกค้ามารับ) ไม่มีผลกับเงิน/ราคา
        // ร้านที่ GP pickup ต่างจริง → สร้างเป็นช่องทางแยก (เช่น grab_pickup) ในหน้าจัดการ
        type: 'delivery',
        table_id: null,
        delivery_platform_id: this.form.delivery_platform_id,
        // ลูกค้ารับเอง → GP อัตรารับเองของช่องทาง (platform ที่เปิด has_pickup เท่านั้น)
        is_pickup: (this.selectedPlatform?.has_pickup && this.form.is_pickup) ? 1 : 0,
        external_order_ref: this.form.external_order_ref || null,
        customer_id: null,
        customer_name: this.selectedPlatform ? this.selectedPlatform.name : '',
        telephone: '',
        pickup_at: null,
        note: this.form.note || ''
      };
      await Sessions.open(payload).then(res => {
        const token = res?.data?.data?.token || '';
        if (token && this.selectedPlatform) {
          // เก็บไว้ให้หน้าคีย์ overlay ราคา + แนบ payload ตอนส่งครัว (BE มี fallback จาก session อยู่แล้ว)
          useShopStore().setSessionPlatform(token, {
            delivery_platform_id: this.selectedPlatform.id,
            external_order_ref: this.form.external_order_ref || '',
            name: this.selectedPlatform.name,
            color: this.selectedPlatform.color || '',
            is_external: true
          });
        }
        this.$toast.success('<b>' + this.$t('Success') + '</b> ' + this.$t('Session opened'));
        $(this.$refs.modal).modal('hide');
        this.$emit('saved', { token });
      }).catch(err => {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      }).finally(() => {
        this.loadingSave = false;
      });
    }
  },
  mounted() {
    $(this.$refs.modal).modal('show');
    $(this.$refs.modal).on('hidden.bs.modal', () => this.$emit('close'));
    this.fetchPlatforms();
  }
};
</script>
